import React, { useState, useRef } from 'react';
import { CheckCircle, Send, Loader2, Upload, Link as LinkIcon, X, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import Layout from '../components/layout/Layout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ShareYourStoryPage() {
  const [form, setForm] = useState({
    name: '',
    title: '',
    company: '',
    quote: '',
    result_badge: '',
    email: '',
    photo_url: '',
    consent: true,
  });
  const [photoMode, setPhotoMode] = useState('upload'); // 'upload' | 'url'
  const [photoUploading, setPhotoUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const onPhotoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Please choose an image file (JPG, PNG, WEBP, or GIF).'); return; }
    if (file.size > 5 * 1024 * 1024) { setError('Photo must be 5 MB or smaller.'); return; }
    setError(''); setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/testimonials/upload-photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm((f) => ({ ...f, photo_url: res.data.photo_url }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Photo upload failed. Please try again.');
    } finally {
      setPhotoUploading(false);
    }
  };

  const clearPhoto = () => {
    setForm((f) => ({ ...f, photo_url: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const quoteLen = (form.quote || '').trim().length;
  const isValid = form.name.trim().length >= 2 && quoteLen >= 20 && quoteLen <= 1500 && form.consent;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!isValid) {
      if (form.name.trim().length < 2) setError('Please share your name');
      else if (quoteLen < 20) setError(`Please write at least 20 characters (currently ${quoteLen}).`);
      else if (quoteLen > 1500) setError(`Please keep your testimonial under 1500 characters (currently ${quoteLen}).`);
      else if (!form.consent) setError('Please confirm consent to publish your testimonial.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/testimonials/submit`, form, { withCredentials: true });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Layout>
        <section className="section-padding bg-[#FAFAFA] min-h-[60vh] flex items-center">
          <div className="container-custom max-w-2xl text-center" data-testid="testimonial-thanks">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={32} className="text-emerald-600" />
            </div>
            <h1 className="heading-1 text-3xl lg:text-4xl text-[#0A0F1C] mb-4">Thank you for sharing your story.</h1>
            <p className="body-text text-[#475569] mb-6">
              Your testimonial is in review. Once approved, it will appear on the homepage to inspire the next entrepreneur thinking about their first move.
            </p>
            <a href="/" className="btn-primary-blue inline-flex items-center gap-2" data-testid="testimonial-home-link">Back to Home</a>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="section-padding bg-[#FAFAFA]" data-testid="share-your-story-page">
        <div className="container-custom max-w-3xl">
          <div className="text-center mb-8">
            <span className="overline mb-3 inline-block">Share Your Story</span>
            <h1 className="heading-1 text-3xl lg:text-5xl text-[#0A0F1C] mb-4">Did the work change something for you?</h1>
            <p className="body-text text-[#475569] max-w-2xl mx-auto">
              If a session, a resource, or a conversation moved you forward, tell us. Your words help the next person decide to start.
            </p>
          </div>

          <form onSubmit={onSubmit} className="bg-white border border-[#E2E8F0] rounded-xl p-6 lg:p-8 space-y-5 shadow-sm">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Your name *" v={form.name} onChange={update('name')} required placeholder="First Last" testid="testimonial-name" />
              <Field label="Email (optional, not displayed)" type="email" v={form.email} onChange={update('email')} placeholder="you@example.com" testid="testimonial-email" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Your title or role" v={form.title} onChange={update('title')} placeholder="Founder / Coach / Consultant" testid="testimonial-title" />
              <Field label="Company (optional)" v={form.company} onChange={update('company')} placeholder="Your business name" testid="testimonial-company" />
            </div>
            <Field
              label="Your story *"
              type="textarea"
              v={form.quote}
              onChange={update('quote')}
              required
              minLength={20}
              maxLength={1500}
              rows={6}
              placeholder="Share what you were stuck on, what changed, and what's different now. The more specific, the more it helps the next entrepreneur."
              testid="testimonial-quote"
            />
            <div className="text-[11px] text-[#94A3B8] flex items-center justify-between -mt-3" data-testid="testimonial-quote-meta">
              <span className={quoteLen >= 20 ? 'text-emerald-600' : 'text-[#94A3B8]'}>
                {quoteLen >= 20 ? '✓ Long enough' : `${20 - quoteLen} more characters needed`}
              </span>
              <span>{quoteLen}/1500</span>
            </div>
            <Field
              label="One-line result (optional, shows as a badge)"
              v={form.result_badge}
              onChange={update('result_badge')}
              placeholder="e.g. Launched in 90 days · Doubled revenue · Hired first 3 people"
              testid="testimonial-badge"
            />

            {/* Photo upload (optional) */}
            <div className="border-t border-[#E2E8F0] pt-5">
              <div className="flex items-center gap-2 mb-1.5">
                <ImageIcon size={14} className="text-[#1D4ED8]" />
                <span className="text-xs font-bold uppercase tracking-wider text-[#475569]">Your photo (optional)</span>
              </div>
              <p className="text-xs text-[#475569] leading-relaxed mb-3">
                Photos are not always used, but feel free to share one if you're okay with it being displayed alongside your story. Headshots and natural-light photos work best. JPG, PNG, WEBP, or GIF up to 5 MB.
              </p>
              <div className="flex gap-1 mb-3 bg-[#F1F5F9] rounded-md p-1 inline-flex">
                <button
                  type="button"
                  onClick={() => setPhotoMode('upload')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-bold ${photoMode === 'upload' ? 'bg-white text-[#1D4ED8] shadow-sm' : 'text-[#475569]'}`}
                  data-testid="photo-mode-upload"
                >
                  <Upload size={12} /> Upload from device
                </button>
                <button
                  type="button"
                  onClick={() => setPhotoMode('url')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-bold ${photoMode === 'url' ? 'bg-white text-[#1D4ED8] shadow-sm' : 'text-[#475569]'}`}
                  data-testid="photo-mode-url"
                >
                  <LinkIcon size={12} /> Paste URL
                </button>
              </div>

              {photoMode === 'upload' ? (
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={onPhotoFile}
                    className="block w-full text-xs text-[#475569] file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-xs file:font-bold file:bg-[#EFF6FF] file:text-[#1D4ED8] hover:file:bg-[#DBEAFE] file:cursor-pointer"
                    data-testid="testimonial-photo-file"
                  />
                  {photoUploading && (
                    <p className="text-xs text-[#475569] mt-2 flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Uploading…</p>
                  )}
                </div>
              ) : (
                <input
                  type="url"
                  value={form.photo_url}
                  onChange={(e) => setForm({ ...form, photo_url: e.target.value })}
                  placeholder="https://example.com/your-photo.jpg"
                  className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm"
                  data-testid="testimonial-photo-url"
                />
              )}

              {form.photo_url && (
                <div className="mt-3 flex items-center gap-3" data-testid="photo-preview">
                  <img
                    src={form.photo_url.startsWith('http') ? form.photo_url : `${API_URL}${form.photo_url}`}
                    alt="Your testimonial photo preview"
                    className="w-16 h-16 object-cover rounded-full border-2 border-[#E2E8F0]"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold text-emerald-700 flex items-center gap-1">
                      <CheckCircle size={12} /> Photo attached
                    </div>
                    <div className="text-[11px] text-[#94A3B8] truncate font-mono">{form.photo_url}</div>
                  </div>
                  <button type="button" onClick={clearPhoto} className="text-[#94A3B8] hover:text-red-600 p-1" aria-label="Remove photo" data-testid="photo-clear">
                    <X size={16} />
                  </button>
                </div>
              )}
            </div>

            <label className="flex items-start gap-3 text-sm text-[#475569] cursor-pointer">
              <input type="checkbox" checked={form.consent} onChange={update('consent')} className="mt-1" data-testid="testimonial-consent" required />
              <span>I give DCA Enterprises Inc. permission to publish this testimonial on the website. I understand it will not be edited materially.</span>
            </label>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-md" data-testid="testimonial-error">{error}</div>
            )}
            <button
              type="submit"
              disabled={submitting || !isValid}
              className="btn-primary-orange inline-flex items-center justify-center gap-2 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="testimonial-submit"
            >
              {submitting ? <><Loader2 size={16} className="animate-spin" /> Submitting…</> : <>Share My Story <Send size={16} /></>}
            </button>
          </form>
        </div>
      </section>
    </Layout>
  );
}

function Field({ label, v, onChange, required, type = 'text', placeholder, rows = 3, minLength, maxLength, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-[#475569] mb-1.5 inline-block">{label}</span>
      {type === 'textarea' ? (
        <textarea
          value={v || ''}
          onChange={onChange}
          required={required}
          rows={rows}
          minLength={minLength}
          maxLength={maxLength}
          placeholder={placeholder}
          className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md focus:border-[#1D4ED8] focus:ring-2 focus:ring-[#1D4ED8]/20 outline-none text-sm text-[#0A0F1C] resize-y"
          data-testid={testid}
        />
      ) : (
        <input
          type={type}
          value={v || ''}
          onChange={onChange}
          required={required}
          placeholder={placeholder}
          className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md focus:border-[#1D4ED8] focus:ring-2 focus:ring-[#1D4ED8]/20 outline-none text-sm text-[#0A0F1C]"
          data-testid={testid}
        />
      )}
    </label>
  );
}
